import React, { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { currencyFormatter, Button } from '@cloudcar-app/cloudcar-components';
import { Row, Col, Spin } from 'antd';
import ReactGa from 'react-ga4';
import useAnalytics from 'routes/GoogleAnalytics/useAnalytics';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import operations from 'apollo/operations';
import {
  OperationalCosts,
  AvailableProcedures,
  CreateOperationalCostsInput,
  SelectedProceduresInput,
  LitePurchaseIntent,
} from 'apollo/types';
import {
  selectedProceduresTotalCost,
  stepList,
  useMocksCache,
} from 'apollo/cache';
import { NextButton } from 'components/ui';
import { OperationalCostsTable } from 'components/procedures';
import getStepIndexByPathname from 'utils/getStepIndexByPathname';
import baseProceduresInfo, { ProcedureInfo } from 'utils/proceduresInfo';
import { DownloadOutlined } from '@ant-design/icons';
import tagPriceMapper from 'utils/kaufmann/dealershipMapper';
import {
  vehicleInspectionTypes,
  vehicleTypes,
} from 'utils/kaufmann/vehicleTypesMappers';
import Header from 'components/ui/Header';
import styles from './OtherProcedures.module.scss';

const OtherProcedures: FC = (): JSX.Element => {
  const history = useHistory();
  const initialized = useAnalytics();
  const vehicleTypesWithSpecialProcedures = ['Minibus', 'Bus particular'];
  const [proceduresInViewMode, setProceduresInViewMode] = useState(true);
  const [proceduresData, setProceduresData] = useState<null | OperationalCosts>(
    null,
  );

  const [proceduresInfo, setProceduresInfo] =
    useState<ProcedureInfo[]>(baseProceduresInfo);

  const [selectedVehicleType, setSelectedVehicleType] = useState<string>(
    vehicleTypes[0].value,
  );

  const [voucherProceduresData, setVoucherProceduresData] = useState<
    SelectedProceduresInput[]
  >([]);

  const [voucherPollingActive, setVoucherPollingActive] = useState(true);
  const [tagPrice, setTagPrice] = useState<number>(tagPriceMapper[0].price);
  const [soapPrice, setSoapPrice] = useState<number>(vehicleTypes[0].price);

  const [vehicleInspectionPrice, setVehicleInspectionPrice] =
    useState<number>(0);

  const [enableVoucherDownload, setEnableVoucherDownload] = useState(false);
  const [disableNextButton, setDisableNextButton] = useState(false);
  const [selectedSpecialOperationalCosts, setSelectedSpecialOperationalCosts] =
    useState(false);

  const steps = useReactiveVar(stepList);
  const currentStepIndex = getStepIndexByPathname(
    history.location.pathname,
    steps,
  );
  const totalProceduresCost = useReactiveVar(selectedProceduresTotalCost);

  const [
    selectProcedures,
    { data: selectedProceduresData, loading: selectProceduresLoading },
  ] = useMutation<
    { createOperationalCosts: OperationalCosts },
    { createOperationalCostsInput: CreateOperationalCostsInput }
  >(operations.SELECT_PROCEDURES);

  const { data, loading } = useQuery<{
    operationalCosts: OperationalCosts;
  }>(operations.GET_OPERATIONAL_COSTS, {
    fetchPolicy: 'cache-and-network',
  });

  const [callAnalytics, { loading: analyticsLoading }] = useMutation(
    operations.CALL_ANALYTICS,
  );

  const { data: litePurchaseIntentData, loading: litePurchaseIntentLoading } =
    useQuery<{
      unitedLitePurchaseIntent: LitePurchaseIntent;
    }>(operations.GET_LITE_PURCHASE_INTENT, {
      fetchPolicy: 'cache-and-network',
    });

  const { data: voucherData } = useQuery<{
    operationalCostsVoucher: string;
    operationalCostsVoucherInput: SelectedProceduresInput[];
  }>(operations.GET_VOUCHER, {
    variables: {
      operationalCostsVoucherInput: voucherProceduresData,
    },
    fetchPolicy: 'no-cache',
    skip: !enableVoucherDownload || !voucherProceduresData.length,
    pollInterval: voucherPollingActive ? 100000 : 0,
  });

  const next = async () => {
    if (proceduresInViewMode) {
      if (
        !litePurchaseIntentData?.unitedLitePurchaseIntent?.insurance
          ?.insuranceInput
      ) {
        history.push({
          pathname: '/',
          state: {
            from: history.location,
          },
        });
      } else if (
        litePurchaseIntentData?.unitedLitePurchaseIntent?.insurance
          ?.insuranceInput
      ) {
        history.push({
          pathname: steps[currentStepIndex + 1].route,
          state: {
            from: history.location,
          },
        });
      }
    } else if (!litePurchaseIntentData?.unitedLitePurchaseIntent?.insurance) {
      history.push({
        pathname: '/',
        state: {
          from: history.location,
        },
      });
    } else {
      await callAnalytics({
        variables: {
          event: 'evento_vista_seguros',
        },
      });
      history.push({
        pathname: steps[currentStepIndex + 1].route,
        state: {
          from: history.location,
        },
      });
    }
  };

  const createProcedures = async () => {
    if (useMocksCache()) {
      next();
    }
    if (enableVoucherDownload && !proceduresInViewMode) {
      await selectProcedures({
        variables: {
          createOperationalCostsInput: {
            selectedProcedures: proceduresInfo
              .filter((procedure) =>
                Object.values(AvailableProcedures).includes(procedure.key),
              )
              .map((procedure) => ({
                name: procedure.key,
                price: procedure.price,
                selected: procedure.selected || false,
              })),
          },
        },
      });
      if (initialized) {
        ReactGa.event({
          category: 'Procedures',
          action: 'Select',
          label: 'Navigation',
        });
      }
    }
  };

  const downloadPdf = async () => {
    const linkSource = `data:application/pdf;base64,${
      voucherData?.operationalCostsVoucher as string
    }`;

    const downloadLink = document.createElement('a');

    const fileName = `${
      localStorage.getItem('customerRut') || ''
    }-tramites.pdf`;
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();

    await callAnalytics({
      variables: {
        event: 'evento_descarga_pdf_tramite',
      },
    });
  };

  const setVoucherPollingActiveFunction = () => {
    if (
      (voucherData && voucherData.operationalCostsVoucher) ||
      enableVoucherDownload
    ) {
      setVoucherPollingActive(false);
    }
  };

  const setProceduresDataFunction = () => {
    if (!loading && !litePurchaseIntentLoading) {
      if (litePurchaseIntentData?.unitedLitePurchaseIntent.operationalCosts) {
        const procedureData =
          litePurchaseIntentData?.unitedLitePurchaseIntent.operationalCosts;
        setProceduresData(procedureData);
      } else if (data && data.operationalCosts) {
        const procedureData = data.operationalCosts;
        setProceduresData(procedureData);
      }
    }
  };

  const setDisableNextButtonFunction = () => {
    if (
      !proceduresInViewMode &&
      (selectProceduresLoading ||
        !(
          selectedProceduresData &&
          selectedProceduresData.createOperationalCosts
        ))
    ) {
      setDisableNextButton(true);
    } else {
      setDisableNextButton(false);
    }
  };

  const setVoucherProceduresDataFunction = () => {
    if (enableVoucherDownload) {
      const voucherDataInput = proceduresInfo.map((procedure) => {
        return {
          price: procedure.price,
          name: procedure.key,
          selected: procedure.selected,
        };
      });

      setVoucherProceduresData(voucherDataInput);
    }
  };

  useEffect(() => {
    if (localStorage.getItem('detailsInViewMode')) {
      setProceduresInViewMode(true);
    } else {
      setProceduresInViewMode(false);
    }
  }, []);

  useEffect(() => {
    setProceduresDataFunction();
  }, [data, loading, litePurchaseIntentData, litePurchaseIntentLoading]);

  useEffect(() => {
    setVoucherProceduresDataFunction();
  }, [enableVoucherDownload]);

  useEffect(() => {
    createProcedures();
  }, [enableVoucherDownload]);

  useEffect(() => {
    setVoucherPollingActiveFunction();
  }, [voucherData, enableVoucherDownload]);

  useEffect(() => {
    setDisableNextButtonFunction();
  }, [proceduresInViewMode, selectProceduresLoading, selectedProceduresData]);

  useEffect(() => {
    if (
      selectedSpecialOperationalCosts &&
      proceduresData &&
      proceduresData.procedures &&
      proceduresData.procedures.length &&
      tagPrice &&
      soapPrice >= 0
    ) {
      const newProceduresInfo = [...proceduresInfo];
      let parsedNewData = newProceduresInfo.map((procedureInfo) => {
        const procedureData = proceduresData.procedures.find(
          (procedure) => procedure.name === procedureInfo.key,
        );
        if (procedureData) {
          if (procedureInfo.key === AvailableProcedures.ObligatoryInsurance) {
            const enabledSoap =
              procedureData.selected &&
              litePurchaseIntentData?.unitedLitePurchaseIntent
                .obligatoryInsurance;
            if (
              !vehicleTypesWithSpecialProcedures.includes(
                selectedVehicleType,
              ) &&
              enabledSoap
            ) {
              return {
                ...procedureInfo,
                price: soapPrice,
                selected: true,
                disabled: true,
              };
            }
            return {
              ...procedureInfo,
              price: soapPrice,
              selected: false,
              disabled: true,
            };
          }
          if (procedureInfo.key === AvailableProcedures.Tag) {
            const enabledTag =
              procedureData.selected &&
              litePurchaseIntentData?.unitedLitePurchaseIntent.tag;
            if (enabledTag) {
              return {
                ...procedureInfo,
                price: tagPrice,
                selected: true,
                disabled: true,
              };
            }
            return {
              ...procedureInfo,
              price: tagPrice,
              selected: false,
              disabled: true,
            };
          }
          if (procedureData.name === AvailableProcedures.CirculationPermit) {
            if (
              !vehicleTypesWithSpecialProcedures.includes(selectedVehicleType)
            ) {
              return {
                ...procedureInfo,
                price: procedureData.price,
                selected: true,
                disabled: true,
              };
            }
            return {
              ...procedureInfo,
              price: procedureData.price,
              selected: false,
              disabled: true,
            };
          }

          if (procedureData.name === AvailableProcedures.GreenTax) {
            if (procedureData.price > 0) {
              return {
                ...procedureInfo,
                price: procedureData.price,
                selected: true,
                disabled: true,
              };
            }
            return {
              ...procedureInfo,
              price: procedureData.price,
              selected: false,
              disabled: true,
            };
          }

          return {
            ...procedureInfo,
            price: procedureData.price,
            selected: procedureData.selected,
          };
        }
        if (
          procedureInfo.key === AvailableProcedures.VehicleInspection &&
          vehicleTypesWithSpecialProcedures.includes(selectedVehicleType)
        ) {
          return {
            ...procedureInfo,
            price: vehicleInspectionPrice,
            selected: true,
            disabled: true,
          };
        }
        return {
          ...procedureInfo,
        };
      });

      parsedNewData = parsedNewData.filter(
        (procedure) => procedure !== null && procedure.selected,
      );

      const total = (parsedNewData as ProcedureInfo[]).reduce(
        (accumulatedCost: number, currentProcedure: ProcedureInfo) =>
          accumulatedCost + currentProcedure.price,
        0,
      );

      setEnableVoucherDownload(true);
      selectedProceduresTotalCost(total);
      setProceduresInfo(parsedNewData as ProcedureInfo[]);
    }
  }, [proceduresData, litePurchaseIntentData, selectedSpecialOperationalCosts]);

  useEffect(() => {
    const dealer = localStorage.getItem('dealer');
    const dealershipOptionsKeys = tagPriceMapper.map((option) => option.value);
    const vehicleTypesKeys = vehicleTypes.map((option) => option.value);
    let dealershipValue = tagPriceMapper[0].value;
    let vehicleTypeValue = vehicleTypes[0].value;

    if (dealer && dealershipOptionsKeys.includes(dealer)) {
      dealershipValue = dealer;
    }

    setTagPrice(
      tagPriceMapper.find((option) => option.value === dealershipValue)
        ?.price as number,
    );

    let vehicleType;

    if (litePurchaseIntentData?.unitedLitePurchaseIntent?.car) {
      vehicleType =
        litePurchaseIntentData.unitedLitePurchaseIntent.car.vehicleType;
    }

    if (vehicleType && vehicleTypesKeys.includes(vehicleType)) {
      vehicleTypeValue = vehicleType;
    }

    setSelectedVehicleType(vehicleTypeValue);
    setSoapPrice(
      vehicleTypes.find((option) => option.value === vehicleTypeValue)
        ?.price as number,
    );
    if (vehicleTypesWithSpecialProcedures.includes(vehicleTypeValue)) {
      setVehicleInspectionPrice(
        vehicleInspectionTypes.find(
          (option) => option.value === vehicleTypeValue,
        )?.price as number,
      );
    }
    setSelectedSpecialOperationalCosts(true);
  }, [litePurchaseIntentData]);

  const getButtonText = () => {
    if (litePurchaseIntentData?.unitedLitePurchaseIntent?.insurance) {
      return 'Información seguros';
    }
    return 'Finalizar';
  };

  const downloadButton = () => (
    <Button
      icon={<DownloadOutlined />}
      disabled={
        !(
          voucherData &&
          voucherData.operationalCostsVoucher &&
          voucherData.operationalCostsVoucher
        )
      }
      loading={
        !(
          voucherData &&
          voucherData.operationalCostsVoucher &&
          voucherData.operationalCostsVoucher
        )
      }
      onClick={() => downloadPdf()}
    >
      Descargar PDF
    </Button>
  );

  return (
    <Spin spinning={loading}>
      <Header downloadButton={downloadButton()} />
      <div>
        {proceduresData && (
          <div>
            <div className={styles.ProceduresRow}>
              <OperationalCostsTable data={proceduresInfo} loading={loading} />
            </div>
            <div className={styles.PanelRowContainer}>
              <Row
                justify="end"
                align="middle"
                className={styles.ProceduresDetail}
              >
                <Col>
                  <p>Total impuestos y trámites</p>
                </Col>
              </Row>
              <Row
                justify="end"
                align="middle"
                className={styles.ProceduresDetail}
              >
                <Col>
                  {totalProceduresCost ? (
                    <h4 className={styles.Price}>
                      {currencyFormatter().format(totalProceduresCost || 0)}
                    </h4>
                  ) : (
                    '-'
                  )}
                </Col>
              </Row>
            </div>
          </div>
        )}
        <NextButton
          headerContent="Los gastos de impuestos, seguros, aranceles de inscripción en el Registro Nacional de Vehículos Motorizados, y los demás gastos adicionales mencionados, son solo referenciales. Si por efecto de la fecha de facturación o por cualquier otra causa, se producen diferencias entre estos valores referenciales y los que efectivamente se deban pagar, éstos serán de cargo y costo del cliente."
          text={getButtonText()}
          htmlType="submit"
          onClick={next}
          disabled={disableNextButton || analyticsLoading}
          loading={disableNextButton || analyticsLoading}
        />
      </div>
    </Spin>
  );
};

export default OtherProcedures;
